import { useState } from "react";
import { api, apiError } from "@/lib/api";

const ROLES = [
  { value: "worker", label: "Worker (pharmacist, technician, clerk)" },
  { value: "pharmacy", label: "Pharmacy" },
  { value: "admin", label: "Administrator" },
];

export const CreateAccountPanel = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "worker", password: "" });
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const { data } = await api.post("/admin/users", form);
      setMsg({ ok: true, text: `Created ${data.email} as ${data.role}. Share the password securely.` });
      setForm({ name: "", email: "", phone: "", role: "worker", password: "" });
      await onCreated();
    } catch (e) {
      setMsg({ ok: false, text: apiError(e) });
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="btn-primary" data-testid="open-create-account">
        Create account
      </button>
    );
  }

  return (
    <div className="card-surface w-full space-y-4 p-5" data-testid="create-account-panel">
      <h2 className="font-display text-lg font-semibold">Create an account</h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="field-label" htmlFor="ca-name">Name</label>
          <input id="ca-name" className="field-input" value={form.name}
                 onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="create-account-name" />
        </div>
        <div>
          <label className="field-label" htmlFor="ca-email">Email</label>
          <input id="ca-email" className="field-input" type="email" value={form.email}
                 onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="create-account-email" />
        </div>
        <div>
          <label className="field-label" htmlFor="ca-phone">Phone (optional)</label>
          <input id="ca-phone" className="field-input" value={form.phone}
                 onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="create-account-phone" />
        </div>
        <div>
          <label className="field-label" htmlFor="ca-role">Account type</label>
          <select id="ca-role" className="field-input" value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="create-account-role">
            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="field-label" htmlFor="ca-password">Temporary password (min 8 characters)</label>
          <input id="ca-password" className="field-input" value={form.password} minLength={8}
                 onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="create-account-password" />
        </div>
      </div>
      <p className="text-xs text-slate-500">
        The new user accepts the Terms and Privacy Policy themselves the first time they sign in — we
        cannot accept them on their behalf. Pharmacies and pharmacists also verify their own NPI then.
      </p>
      {msg && (
        <p className={`rounded-lg px-3 py-2 text-sm ${msg.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}
           data-testid="create-account-message">
          {msg.text}
        </p>
      )}
      <div className="flex flex-wrap gap-3">
        <button onClick={submit} disabled={busy || !form.email || !form.name || form.password.length < 8}
                className="btn-primary" data-testid="create-account-submit">
          {busy ? "Creating…" : "Create account"}
        </button>
        <button onClick={() => setOpen(false)} className="btn-ghost" data-testid="create-account-cancel">
          Close
        </button>
      </div>
    </div>
  );
};

export const ResetPasswordControl = ({ userId, email, testIdSuffix }) => {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (payload, successText) => {
    setBusy(true);
    setMsg(null);
    try {
      await api.post(`/admin/users/${userId}/reset-password`, payload);
      setMsg({ ok: true, text: successText });
      setPassword("");
    } catch (e) {
      setMsg({ ok: false, text: apiError(e) });
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button onClick={() => { setOpen(true); setMsg(null); }} className="text-sm font-medium text-brand hover:underline"
              data-testid={`reset-password-${testIdSuffix}`}>
        Reset password
      </button>
    );
  }

  return (
    <div className="min-w-[16rem] space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3"
         data-testid={`reset-password-panel-${testIdSuffix}`}>
      <p className="text-xs text-slate-600">Reset the password for <strong>{email}</strong>.</p>
      <input className="field-input py-1.5 text-xs" type="text" placeholder="Temporary password (min 8)"
             value={password} onChange={(e) => setPassword(e.target.value)}
             data-testid={`reset-password-input-${testIdSuffix}`} />
      {msg && (
        <p className={`text-xs ${msg.ok ? "text-emerald-700" : "text-red-700"}`}
           data-testid={`reset-password-message-${testIdSuffix}`}>
          {msg.text}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => run({ new_password: password }, "Temporary password set.")}
                disabled={busy || password.length < 8}
                className="rounded-lg bg-brand px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
                data-testid={`reset-password-set-${testIdSuffix}`}>
          Set password
        </button>
        <button onClick={() => run({ send_email: true }, "Reset link emailed.")} disabled={busy}
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700"
                data-testid={`reset-password-email-${testIdSuffix}`}>
          Email reset link
        </button>
        <button onClick={() => setOpen(false)} className="text-xs font-medium text-slate-600"
                data-testid={`reset-password-close-${testIdSuffix}`}>
          Close
        </button>
      </div>
    </div>
  );
};
